import { createContext, useContext } from "react"

/**
 * ROLE SCOPE — the second global scope, beside the organization.
 *
 * `null` means "All roles", and it is the default with no provider mounted. That
 * matters: every generated API hook reads this context, and the embed tree has no
 * `RoleScopeProvider` (it has no Clerk either). A missing provider must therefore
 * degrade to the unscoped behaviour rather than throw.
 *
 * **This is a LEAF module on purpose.** `RoleScopeProvider` imports it; it imports
 * nothing of the provider tree. The generated API client
 * (`common/api/generated/*.ts`) reads `useRoleScopeId` from here, and the same
 * split already exists for `api-auth-context` so the API chunk does not drag
 * `AppProviders` in behind it.
 *
 * `role` on its own is a different concept in this codebase — `OrganizationRole`
 * (`common/layouts/MainLayout/types.ts`) is Clerk's `"org:admin" | "org:member"`,
 * and every Settings navigation item carries a `roles: OrganizationRole[]`. Nothing
 * here is ever named `role` alone.
 */
/**
 * How a scope change should enter the browser's history.
 *
 * TWO different acts, not one with a flag. A viewer PICKING a Role is a
 * navigation and gets its own entry, so Back undoes it — that is the whole point
 * of the scope living in the URL. A CORRECTION — dropping a scope whose Role has
 * been deleted — must replace, or Back returns to the broken URL, the correction
 * fires again, and the viewer is pinned.
 */
export type RoleScopeHistory = "push" | "replace"

/**
 * Whether a selection can be applied yet, and if not, WHY NOT.
 *
 * 🚨 **THREE STATES, BECAUSE "NOT YET" AND "NOT AT ALL" CALL FOR OPPOSITE
 * SURFACES AND CLERK REPORTS THEM WITH THE SAME VALUE.** `useAuth()` answers
 * `orgId: undefined` for as long as `isLoaded` is `false`, and `orgId: null`
 * once loaded with no active organization. Collapsing the two into one boolean
 * is what makes a LOADING window state "No active organisation" — a definite
 * wrong answer covering an unknown one, which is the same defect as the silent
 * discard it replaces, one layer up.
 *
 * - `resolving` — Clerk has not answered. WAIT. Say "resolving", never
 *   "absent", and never claim a scope: the answer is seconds away and the
 *   control goes live on its own when it lands.
 * - `no-organization` — Clerk HAS answered and names none. This is the only
 *   state the "No active organisation" copy is honest for.
 * - `available` — a press will be honoured.
 *
 * Only `available` permits {@link RoleScopeState.setRoleScopeId} to write; a
 * control that offers the press in either other state is lying to the viewer.
 * A scope is stored BESIDE the organization it was chosen in, so with no
 * organization there is no entry to write and none that could be read back.
 */
export type RoleScopeAvailability = "resolving" | "no-organization" | "available"

export interface RoleScopeState {
  /** The selected Role's id, or `null` for "All roles". */
  readonly roleScopeId: string | null
  /**
   * Can a selection be applied AT ALL right now, and if not, why not?
   *
   * This exists because the alternative was silent. The setter used to fold "the
   * viewer chose All roles" and "we have no organization" into one write of
   * `null`, so a press was accepted, discarded, and reported as a successful
   * selection of "All roles" — no error, no toast, nothing to search for. The
   * refusal is now a value the UI must render rather than a branch it cannot
   * see. See {@link RoleScopeAvailability} for why it is not a boolean.
   */
  readonly availability: RoleScopeAvailability
  /**
   * Select a Role, or `null` to go back to "All roles".
   *
   * 🚨 **On a route whose PATH declares the scope this NAVIGATES**, because on
   * those routes the URL is the source of truth (`role-scope-route.ts`) and a
   * write that left the two disagreeing would be undone by the next render with
   * nothing to show for it. On every other route it only records the selection.
   * That is why there is one setter and not two: the disagreement is not
   * expressible.
   */
  readonly setRoleScopeId: (
    roleScopeId: string | null,
    options?: { readonly history?: RoleScopeHistory }
  ) => void
}

/**
 * The no-provider default — the embed tree, which has no Clerk and no Roles
 * surface.
 *
 * `no-organization` rather than `resolving`, because it is SETTLED: there is no
 * Clerk here to answer later, `setRoleScopeId` is a permanent no-op, and a
 * control mounted against this context genuinely cannot ever apply a selection.
 * `resolving` would promise an answer that is never coming, and `available`
 * would be the same lie the provider used to tell, one layer down.
 */
const ALL_ROLES_STATE: RoleScopeState = {
  roleScopeId: null,
  availability: "no-organization",
  setRoleScopeId: () => {}
}

export const RoleScopeContext = createContext<RoleScopeState>(ALL_ROLES_STATE)

/** The full scope state — read this in the picker. */
export function useRoleScope(): RoleScopeState {
  return useContext(RoleScopeContext)
}

/**
 * The selected Role's id, or `null` for "All roles".
 *
 * 🚨 **Every generated query hook calls this, and that is what keeps the cache and
 * the wire in step.** The id lands in the React Query key AND, through
 * `useRequest`, in the request header — both read in the SAME render pass, from
 * the same context. A module-level variable read at send time would look like the
 * same "one edit" and would not be: a request in flight across a scope change
 * would be stored under the key of one scope carrying the data of another, with
 * nothing to notice it afterwards.
 */
export function useRoleScopeId(): string | null {
  return useContext(RoleScopeContext).roleScopeId
}
